import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Search as SearchIcon, GitCompare, Store, Scale, ShieldCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

function PageShell({ title, subtitle, children }) {
  return (
    <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <h1 className="font-heading text-3xl font-bold tracking-tighter text-slate-900 sm:text-4xl">{title}</h1>
        {subtitle && <p className="mt-3 text-lg text-slate-600">{subtitle}</p>}
      </motion.div>
      <div className="mt-10 space-y-6 text-slate-700 leading-relaxed">{children}</div>
    </div>
  );
}

function Prose({ blocks }) {
  return blocks.map((b, i) =>
    b.h ? <h2 key={i} className="mt-8 font-heading text-xl font-semibold tracking-tight text-slate-900">{b.h}</h2>
        : <p key={i} className="text-slate-600">{b.p}</p>
  );
}

export function About() {
  return (
    <PageShell title="About PartFinder.sk" subtitle="We help drivers and workshops across Central Europe find the right part at the best total price.">
      <Prose blocks={[
        { p: "PartFinder.sk is an independent car-parts comparison platform serving Slovakia, the Czech Republic, Poland and Hungary. We aggregate offers from verified retailers so buyers can compare total landed price — including shipping and delivery time — in a single search." },
        { h: "Our mission" },
        { p: "Buying car parts online is confusing: prices exclude shipping, delivery times are hidden and OEM numbers vary between sellers. We bring transparency by normalising and deduplicating catalogs, matching parts by OEM and EAN, and ranking offers purely on value to the buyer." },
        { h: "Why four markets" },
        { p: "Central Europe shares supply chains and vehicle fleets. A part listed in Poland is often cheaper delivered to Slovakia than a domestic offer. By comparing across borders, buyers unlock better prices while sellers reach more customers." },
      ]} />
      <div className="grid gap-4 sm:grid-cols-3">
        {[["4", "Markets"], ["8+", "Verified retailers"], ["50+", "Parts indexed"]].map(([n, l]) => (
          <div key={l} className="rounded-lg border border-border bg-white p-6 text-center">
            <div className="font-heading text-3xl font-bold tracking-tight text-primary">{n}</div>
            <div className="text-sm text-slate-500">{l}</div>
          </div>
        ))}
      </div>
    </PageShell>
  );
}

export function HowItWorks() {
  const { t } = useI18n();
  const steps = [
    [SearchIcon, t("how.s1.title"), t("how.s1.text")],
    [GitCompare, t("how.s2.title"), t("how.s2.text")],
    [Store, t("how.s3.title"), t("how.s3.text")],
  ];
  return (
    <PageShell title={t("how.title")} subtitle={t("how.subtitle")}>
      <div className="grid gap-6">
        {steps.map(([Icon, title, text], i) => (
          <div key={title} className="flex gap-5 rounded-lg border border-border bg-white p-6">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-lg bg-primary text-white"><Icon className="h-6 w-6" /></div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Step {i + 1}</div>
              <h3 className="font-heading text-lg font-semibold tracking-tight text-slate-900">{title}</h3>
              <p className="mt-1 text-slate-600">{text}</p>
            </div>
          </div>
        ))}
      </div>
    </PageShell>
  );
}

export function Methodology() {
  const items = [
    [Scale, "Total price ranking", "Offers are sorted by total landed cost (item price + shipping) in the buyer's context, never by seller fees or commissions."],
    [ShieldCheck, "Seller verification", "Every retailer is checked for VAT registration, a valid business address and delivery reliability before offers go live."],
    [RefreshCw, "Deduplication", "We match products by OEM number and EAN, normalise brand names and merge duplicate listings so you compare the same part across sellers."],
  ];
  return (
    <PageShell title="Methodology" subtitle="How we collect, normalise and rank offers — documented and open.">
      <div className="grid gap-4">
        {items.map(([Icon, title, text]) => (
          <div key={title} className="flex gap-4 rounded-lg border border-border bg-white p-6">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-blue-50 text-primary"><Icon className="h-5 w-5" /></div>
            <div><h3 className="font-heading font-semibold tracking-tight text-slate-900">{title}</h3><p className="mt-1 text-sm text-slate-600">{text}</p></div>
          </div>
        ))}
      </div>
      <Prose blocks={[
        { h: "Data sources" },
        { p: "Offers are ingested via retailer APIs and scheduled XML/CSV feeds. During onboarding we map each partner's fields to our unified schema. The current catalog uses representative demo data while integrations are finalised." },
        { h: "Independence" },
        { p: "We are paid per qualified click, at the same rate for every partner. Ranking is not influenced by how much a seller pays — the buyer always sees the best total-value offer first." },
      ]} />
    </PageShell>
  );
}

const FAQ_ITEMS = [
  ["Is PartFinder free for buyers?", "Yes. Comparing parts and prices is completely free. You buy directly from the retailer with the best offer."],
  ["How do you calculate the total price?", "We add the item price and the shipping cost to your selected market so you can compare offers on an equal basis."],
  ["Are the sellers trustworthy?", "Every partner is verified for VAT registration and delivery reliability before appearing in results. Verified sellers show a check badge."],
  ["Which countries are covered?", "Slovakia, the Czech Republic, Poland and Hungary — with cross-border offers where delivery is available."],
  ["How do I find a part for my exact car?", "Search by OEM number or EAN for an exact match, or add your vehicle to My Garage and search by part name."],
  ["Do you sell parts directly?", "No. PartFinder is an independent comparison service. Purchases are completed on the retailer's own site."],
];

export function FAQ() {
  return (
    <PageShell title="Frequently asked questions" subtitle="Everything you need to know about comparing and buying parts.">
      <Accordion type="single" collapsible className="rounded-lg border border-border bg-white px-6">
        {FAQ_ITEMS.map(([q, a], i) => (
          <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-${i}`}>
            <AccordionTrigger className="text-left font-heading font-semibold tracking-tight text-slate-900">{q}</AccordionTrigger>
            <AccordionContent className="text-slate-600">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </PageShell>
  );
}

export function Contact() {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/contact", form);
    setSent(true);
    toast.success(t("contact.success"));
  };
  return (
    <PageShell title={t("contact.title")} subtitle="Questions, partnership ideas or feedback — we'd love to hear from you.">
      {sent ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="contact-success">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-500 text-white"><Check className="h-7 w-7" /></div>
          <p className="mt-4 font-heading text-lg font-semibold text-slate-900">{t("contact.success")}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="grid gap-4 rounded-lg border border-border bg-white p-8 sm:grid-cols-2">
          <div><label className="text-sm font-medium text-slate-700">{t("auth.name")}</label>
            <input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 w-full rounded-lg border border-border px-4 py-2.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary" /></div>
          <div><label className="text-sm font-medium text-slate-700">{t("auth.email")}</label>
            <input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 w-full rounded-lg border border-border px-4 py-2.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary" /></div>
          <div className="sm:col-span-2"><label className="text-sm font-medium text-slate-700">{t("contact.subject")}</label>
            <input data-testid="contact-subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5 w-full rounded-lg border border-border px-4 py-2.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary" /></div>
          <div className="sm:col-span-2"><label className="text-sm font-medium text-slate-700">{t("partnerform.message")}</label>
            <textarea data-testid="contact-message" required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 w-full rounded-lg border border-border px-4 py-2.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary" /></div>
          <button type="submit" data-testid="contact-submit" className="rounded-full bg-primary px-6 py-3 font-heading font-semibold text-white transition-colors duration-200 hover:bg-blue-600 sm:col-span-2">{t("contact.send")}</button>
        </form>
      )}
    </PageShell>
  );
}

function LegalPage({ title, blocks }) {
  return <PageShell title={title}><Prose blocks={blocks} /></PageShell>;
}

export function Privacy() {
  return <LegalPage title="Privacy Policy" blocks={[
    { p: "This Privacy Policy explains how PartFinder.sk collects and processes personal data in line with the GDPR. This is demo content for a development environment." },
    { h: "Data we collect" }, { p: "Account data (name, email), saved vehicles, favorites and price alerts you create, plus anonymous analytics about how the site is used." },
    { h: "How we use it" }, { p: "To provide comparison features, notify you about price alerts and improve the service. We never sell personal data." },
    { h: "Your rights" }, { p: "You may request access, correction or deletion of your data at any time via our contact page." },
  ]} />;
}
export function Terms() {
  return <LegalPage title="Terms of Service" blocks={[
    { p: "These Terms govern your use of PartFinder.sk. By using the service you agree to them. This is demo content for a development environment." },
    { h: "Comparison service" }, { p: "PartFinder is an independent comparison platform. Purchases are completed on retailer websites; contracts of sale are between you and the retailer." },
    { h: "Accuracy" }, { p: "We strive for accurate pricing and availability but cannot guarantee real-time correctness of third-party data." },
  ]} />;
}
export function Cookies() {
  return <LegalPage title="Cookie Policy" blocks={[
    { p: "PartFinder.sk uses cookies to keep you logged in, remember your market and language, and measure usage. This is demo content for a development environment." },
    { h: "Essential cookies" }, { p: "Required for authentication and core functionality. These cannot be disabled." },
    { h: "Analytics cookies" }, { p: "Help us understand how the site is used so we can improve it. You can opt out in your browser settings." },
  ]} />;
}
